import * as React from "react"
import { useQuery, useQueryClient } from "@tanstack/react-query"
import { CheckCircle2, ExternalLink } from "lucide-react"
import { useParams } from "react-router-dom"

import { buildPublicIssueSubmission, getPublicIssue, submitPublicIssue } from "@/api/public-issue"
import type { PublicIssueData } from "@/api/public-issue"
import { Button } from "@/components/shadcn/ui/button"
import { Input } from "@/components/shadcn/ui/input"
import Section from "@/components/vivid-report-editor/Section"
import { reportValidationErrorMessage } from "@/components/vivid-report-editor/report-validation-error"
import {
  emptyReportCreationMedia,
  reindexCreationMediaAfterDynamicGroupDeletion,
  type ReportCreationMedia,
} from "@/components/vivid-report-editor/report-creation-media"
import { validateReportCreation } from "@/components/vivid-report-editor/report-creation-validation"
import type { FormResponse, ResponseValue } from "@/models/new/formResponse.types"
import {
  getDynamicGroupInstances,
  reindexResponsesAfterDynamicGroupDeletion,
} from "@/components/vivid-report-editor/dynamic-group-reindex"
import type { DynamicGroup } from "@/models/new/forms.types"
import { shouldShowElement } from "@/utils/conditionalDisplay"
import { publicFieldMediaValidationMessage } from "./public-field-media-validation"

type ErrorWithStatus = Error & { status?: number; data?: { code?: string } }

const PublicForm = ({
  data,
  refetch,
}: {
  data: PublicIssueData
  refetch: () => Promise<unknown>
}) => {
  const params = useParams<{ organizationId: string; formId: string; linkId: string }>()
  const submissionId = React.useRef(crypto.randomUUID())
  const [formResponse, setFormResponse] = React.useState<FormResponse>(
    () => ({ responses: {} }) as FormResponse,
  )
  const [contact, setContact] = React.useState({ name: "", email: "", phone: "" })
  const [website, setWebsite] = React.useState("")
  const [media, setMedia] = React.useState<ReportCreationMedia>(emptyReportCreationMedia)
  const [error, setError] = React.useState<string>()
  const [submitting, setSubmitting] = React.useState(false)
  const [retryLocked, setRetryLocked] = React.useState(false)
  const [refreshRequired, setRefreshRequired] = React.useState(false)
  const uncertainRef = React.useRef(false)
  const [receiptId, setReceiptId] = React.useState<string>()
  const submittingRef = React.useRef(false)
  const attemptRef = React.useRef<{
    body: ReturnType<typeof buildPublicIssueSubmission>
    form: PublicIssueData["form"]
    responses: FormResponse["responses"]
    instances: Record<string, number[]>
    pendingMediaLocators: string[]
  }>()
  const [resolvedInstancesByGroup, setResolvedInstancesByGroup] = React.useState<
    Record<string, number[]>
  >({})
  const handleDynamicGroupInstancesChange = React.useCallback(
    (groupId: string, instances: number[] | null) => {
      setResolvedInstancesByGroup((prev) => {
        if (instances === null) {
          if (!(groupId in prev)) return prev
          const next = { ...prev }
          delete next[groupId]
          return next
        }
        const current = prev[groupId]
        if (
          current &&
          current.length === instances.length &&
          current.every((n, i) => n === instances[i])
        ) {
          return prev
        }
        return { ...prev, [groupId]: instances }
      })
    },
    [],
  )

  const change = (fieldId: string, value: ResponseValue) =>
    setFormResponse((current) => {
      const responses = { ...current.responses }
      if (value == null) delete responses[fieldId]
      else responses[fieldId] = value
      return { ...current, responses }
    })

  const removeInstance = (field: DynamicGroup, instance: number) => {
    const remaining = Array.from(
      new Set([
        ...getDynamicGroupInstances(formResponse.responses, field.id, undefined, [
          ...Object.keys(media.responseUploads),
          ...Object.keys(media.fieldMedia),
        ]),
        ...(resolvedInstancesByGroup[field.id] ?? []),
      ]),
    )
      .filter((value) => value !== instance)
      .sort((a, b) => a - b)
    setFormResponse((current) => ({
      ...current,
      responses: reindexResponsesAfterDynamicGroupDeletion(
        current.responses,
        field.id,
        field.fields,
        instance,
        remaining,
      ),
    }))
    setMedia(reindexCreationMediaAfterDynamicGroupDeletion(media, field.id, instance, remaining))
  }

  const refreshForm = async () => {
    try {
      await refetch()
      attemptRef.current = undefined
      setRefreshRequired(false)
      setRetryLocked(false)
      setError("The form has been refreshed. Review your answers before submitting again.")
    } catch {
      setError("We couldn't refresh the form. Your answers are saved here. Try refreshing again.")
    }
  }

  const submit = async () => {
    if (submittingRef.current) return
    if (refreshRequired) {
      submittingRef.current = true
      setSubmitting(true)
      await refreshForm()
      setSubmitting(false)
      submittingRef.current = false
      return
    }
    const mediaError = attemptRef.current
      ? undefined
      : publicFieldMediaValidationMessage(data.form, formResponse, media, resolvedInstancesByGroup)
    if (mediaError) {
      setError(mediaError)
      return
    }
    const missing = attemptRef.current
      ? []
      : validateReportCreation(data.form, formResponse, media, resolvedInstancesByGroup)
    if (missing.length) {
      setError(
        `Complete ${missing.length === 1 ? "the required question" : "all required questions"} before submitting.`,
      )
      return
    }
    submittingRef.current = true
    setSubmitting(true)
    setRetryLocked(true)
    setError(undefined)
    try {
      const compactContact = Object.fromEntries(
        Object.entries(contact).filter(([, value]) => value.trim()),
      )
      if (!attemptRef.current) {
        attemptRef.current = {
          body: buildPublicIssueSubmission(
            {
              submissionId: submissionId.current,
              formRevision: data.formRevision,
              ...(Object.keys(compactContact).length ? { contact: compactContact } : {}),
              responses: formResponse.responses,
              website,
            },
            media,
          ),
          form: data.form,
          responses: formResponse.responses,
          instances: resolvedInstancesByGroup,
          pendingMediaLocators: [
            ...Object.keys(media.responseUploads),
            ...Object.keys(media.fieldMedia),
          ],
        }
      }
      const result = await submitPublicIssue(
        params as { organizationId: string; formId: string; linkId: string },
        attemptRef.current.body,
      )
      setReceiptId(result.receiptId)
    } catch (caught) {
      const status = (caught as ErrorWithStatus).status
      const details = (caught as ErrorWithStatus).data
      const attempt = attemptRef.current
      // Staleness is checked before idempotent readback on the server. After an
      // uncertain attempt, even PUBLIC_FORM_STALE cannot prove it did not commit.
      if (status === 409 && details?.code === "PUBLIC_FORM_STALE" && !uncertainRef.current) {
        setRefreshRequired(true)
        await refreshForm()
        return
      }
      // Validation and request-limit failures happen before persistence. Let the
      // worker correct answers only when no earlier outcome remains uncertain.
      if (!uncertainRef.current && [400, 413, 422, 429].includes(status ?? 0)) {
        attemptRef.current = undefined
        setRetryLocked(false)
      } else {
        uncertainRef.current = true
      }
      setError(
        status === 403 || status === 404 || status === 410
          ? "Public reporting for this form is no longer available. You can still open it in Vivid."
          : reportValidationErrorMessage(
              details,
              attempt?.form ?? data.form,
              attempt?.responses ?? formResponse.responses,
              "We couldn't confirm your submission. Your submitted answers are frozen so retrying is safe.",
              attempt?.instances ?? resolvedInstancesByGroup,
              undefined,
              attempt?.pendingMediaLocators ?? [
                ...Object.keys(media.responseUploads),
                ...Object.keys(media.fieldMedia),
              ],
            ),
      )
    } finally {
      setSubmitting(false)
      submittingRef.current = false
    }
  }

  if (receiptId) {
    return (
      <main className="mx-auto flex min-h-screen max-w-xl items-center px-5 py-12">
        <div className="w-full rounded-2xl bg-white p-8 shadow-xl shadow-slate-900/10">
          <CheckCircle2 className="h-12 w-12 text-emerald-600" />
          <h1 className="mt-5 text-3xl font-semibold tracking-tight text-slate-950">
            Issue submitted
          </h1>
          <p className="mt-3 text-slate-600">
            Thank you. Your report has been sent to {data.site.name}.
          </p>
          <p className="mt-6 text-sm text-slate-500">Receipt</p>
          <p className="font-medium tabular-nums text-slate-900">{receiptId}</p>
        </div>
      </main>
    )
  }

  return (
    <main className="mx-auto w-full max-w-3xl px-4 py-8 sm:px-6 sm:py-12">
      <header className="mb-8">
        <h1 className="mt-3 text-3xl font-semibold tracking-tight text-slate-950 sm:text-4xl">
          {data.form.title}
        </h1>
        <p className="mt-2 text-slate-600">Reporting to {data.site.name}</p>
        <a
          className="mt-3 inline-flex items-center gap-2 text-sm font-medium text-indigo-700 hover:underline"
          href={data.appUrl}
        >
          <ExternalLink className="h-4 w-4" /> Open in Vivid
        </a>
      </header>

      <fieldset disabled={submitting || retryLocked} className="min-w-0">
        <section className="mb-6 rounded-2xl bg-white p-5 ring-1 ring-slate-200 sm:p-6">
          <h2 className="text-lg font-semibold text-slate-900">
            Your contact details <span className="font-normal text-slate-500">(optional)</span>
          </h2>
          <p className="mt-1 text-sm text-slate-600">
            Add details if you would like the team to follow up with you.
          </p>
          <div className="mt-4 grid gap-4 sm:grid-cols-3">
            {(["name", "email", "phone"] as const).map((key) => (
              <label key={key} className="text-sm font-medium capitalize text-slate-700">
                {key}
                <Input
                  className="mt-1"
                  type={key === "email" ? "email" : key === "phone" ? "tel" : "text"}
                  value={contact[key]}
                  onChange={(event) =>
                    setContact((current) => ({ ...current, [key]: event.target.value }))
                  }
                />
              </label>
            ))}
          </div>
        </section>

        <label
          className="absolute -left-[10000px] top-auto h-px w-px overflow-hidden"
          aria-hidden="true"
        >
          Website
          <input
            name="website"
            tabIndex={-1}
            autoComplete="off"
            value={website}
            onChange={(event) => setWebsite(event.target.value)}
          />
        </label>

        <div className="space-y-6">
          {data.form.fields.map((section) =>
            shouldShowElement(section.conditionalDisplay, formResponse) ? (
              <section
                key={section.id}
                className="rounded-2xl bg-white p-3 ring-1 ring-slate-200 sm:p-5"
              >
                <h2 className="px-3 pt-2 text-xl font-semibold text-slate-900">{section.label}</h2>
                <div className="mt-2 max-sm:[&_.w-1\/2]:w-full">
                  <Section
                    section={section}
                    formResponse={formResponse}
                    onChange={change}
                    onDynamicGroupInstanceRemove={removeInstance}
                    onDynamicGroupInstancesChange={handleDynamicGroupInstancesChange}
                    listOptions={data.listItemsMap}
                    isCreating
                    canCreateFieldMedia
                    creationMedia={media}
                    onCreationMediaChange={setMedia}
                  />
                </div>
              </section>
            ) : null,
          )}
        </div>
      </fieldset>
      {error ? (
        <p role="alert" className="mt-6 rounded-lg bg-red-50 p-4 text-sm font-medium text-red-800">
          {error}
        </p>
      ) : null}
      <div className="mt-6 flex justify-end">
        <Button size="lg" disabled={submitting} onClick={() => void submit()}>
          {refreshRequired
            ? submitting
              ? "Refreshing…"
              : "Refresh form"
            : submitting
              ? "Submitting…"
              : "Submit issue"}
        </Button>
      </div>
    </main>
  )
}

const PublicIssueForm = () => {
  const params = useParams<{ organizationId: string; formId: string; linkId: string }>()
  const queryClient = useQueryClient()
  const queryKey = ["public-issue", params.organizationId, params.formId, params.linkId]
  const query = useQuery({
    queryKey,
    queryFn: ({ signal }) =>
      getPublicIssue(params as { organizationId: string; formId: string; linkId: string }, signal),
    retry: false,
  })

  if (query.isPending)
    return (
      <main className="flex min-h-screen items-center justify-center text-slate-600">
        Loading form…
      </main>
    )
  if (query.isError && !query.data)
    return (
      <main className="flex min-h-screen items-center justify-center px-6 text-center">
        <div>
          <h1 className="text-2xl font-semibold">Form unavailable</h1>
          <p className="mt-2 text-slate-600">This link cannot accept reports right now.</p>
        </div>
      </main>
    )
  if (query.data.mode === "app-only")
    return (
      <main className="flex min-h-screen items-center justify-center px-5">
        <div className="max-w-md rounded-2xl bg-white p-8 text-center shadow-xl">
          <h1 className="text-2xl font-semibold">Continue in Vivid</h1>
          <p className="mt-3 text-slate-600">This form accepts reports in the Vivid app.</p>
          <a
            className="mt-6 inline-flex h-10 items-center justify-center gap-2 rounded-md bg-neutral-900 px-4 py-2 text-sm font-medium text-white hover:bg-neutral-800"
            href={query.data.appUrl}
          >
            <ExternalLink className="h-4 w-4" /> Open in Vivid
          </a>
        </div>
      </main>
    )
  return (
    <PublicForm
      data={query.data}
      refetch={async () => {
        const refreshed = await getPublicIssue(
          params as { organizationId: string; formId: string; linkId: string },
        )
        // Do not replace the mounted draft with an error/app-only screen when
        // refresh fails or the link stops accepting public submissions.
        if (refreshed.mode !== "public") throw new Error("Public form unavailable")
        queryClient.setQueryData(queryKey, refreshed)
      }}
    />
  )
}

export default PublicIssueForm
